import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './BBMappingPage.css'; // Reuse same styles as VendorTypeMappingPage

/**
 * CostTypeMappingPage - Admin tool for mapping master_items to cost types.
 * Each master_item gets a cost_type_id (e.g., מחיר ליחידה, מחיר למטר רבוע).
 * AI suggestions stored in cost_type_mapping_suggestions table (persists across reloads).
 * Admin approves → batch apply writes to master_items.cost_type_id.
 * Access via /admin/cost-type-mapping.
 */
const CostTypeMappingPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [suggestions, setSuggestions] = useState({}); // keyed by master_item_id
  const [itemTypes, setItemTypes] = useState({});
  const [loading, setLoading] = useState(true);

  // UI state
  const [filter, setFilter] = useState('unmapped'); // unmapped, all, pending, mapped, bundles, non-bundles
  const [searchTerm, setSearchTerm] = useState('');
  const [apiKey] = useState(process.env.REACT_APP_CLAUDE_API_KEY || '');
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, costTypesRes, suggestionsRes, typesRes] = await Promise.all([
        supabase
          .from('master_items')
          .select(
            'id, item_name, item_type_id, cost_type_id, is_bundle, bundle_type, estimated_cost, item_options',
          )
          .order('item_name'),
        supabase.from('reference_cost_types').select('id, name').order('name'),
        supabase.from('cost_type_mapping_suggestions').select('*'),
        supabase.from('reference_item_types').select('id, name'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (costTypesRes.error) throw costTypesRes.error;
      if (suggestionsRes.error) throw suggestionsRes.error;
      if (typesRes.error) throw typesRes.error;

      setMasterItems(itemsRes.data || []);
      setCostTypes(costTypesRes.data || []);

      // Index suggestions by master_item_id
      const sugMap = {};
      (suggestionsRes.data || []).forEach(s => {
        sugMap[s.master_item_id] = s;
      });
      setSuggestions(sugMap);

      // Index item types by id
      const typeMap = {};
      (typesRes.data || []).forEach(t => {
        typeMap[t.id] = t.name;
      });
      setItemTypes(typeMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Computed values ─────────────────────────────────────────────

  // Helper: check if an item is "mapped" (has cost_type_id or approved/manual suggestion)
  const isMapped = useCallback(
    itemId => {
      const s = suggestions[itemId];
      const item = masterItems.find(i => i.id === itemId);
      return item?.cost_type_id || (s && (s.status === 'approved' || s.status === 'manual'));
    },
    [suggestions, masterItems],
  );

  // Helper: get the current mapped cost type ID
  const getMappedCostTypeId = useCallback(
    itemId => {
      const s = suggestions[itemId];
      if (s && (s.status === 'approved' || s.status === 'manual')) {
        return s.suggested_cost_type_id;
      }
      const item = masterItems.find(i => i.id === itemId);
      return item?.cost_type_id || '';
    },
    [suggestions, masterItems],
  );

  // Cost type lookup map
  const costTypeMap = useMemo(() => {
    const map = {};
    costTypes.forEach(ct => {
      map[ct.id] = ct;
    });
    return map;
  }, [costTypes]);

  const stats = useMemo(() => {
    const total = masterItems.length;
    const mapped = masterItems.filter(i => isMapped(i.id)).length;
    const pending = Object.values(suggestions).filter(s => s.status === 'pending').length;
    const bundles = masterItems.filter(i => i.is_bundle).length;
    const unmappedNonBundles = masterItems.filter(i => !i.is_bundle && !isMapped(i.id)).length;
    return { total, mapped, unmapped: total - mapped, pending, bundles, unmappedNonBundles };
  }, [masterItems, suggestions, isMapped]);

  // Filter and search items
  const filteredItems = useMemo(() => {
    let items = masterItems;

    switch (filter) {
      case 'unmapped':
        items = items.filter(i => !isMapped(i.id));
        break;
      case 'pending':
        items = items.filter(i => suggestions[i.id]?.status === 'pending');
        break;
      case 'mapped':
        items = items.filter(i => isMapped(i.id));
        break;
      case 'bundles':
        items = items.filter(i => i.is_bundle && !isMapped(i.id));
        break;
      case 'non-bundles':
        items = items.filter(i => !i.is_bundle && !isMapped(i.id));
        break;
      default:
        break;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        i =>
          i.item_name.toLowerCase().includes(term) ||
          (itemTypes[i.item_type_id] || '').toLowerCase().includes(term),
      );
    }

    return items;
  }, [masterItems, filter, searchTerm, suggestions, itemTypes, isMapped]);

  // ─── Actions ─────────────────────────────────────────────────────

  // Approve an AI suggestion
  const handleApprove = async itemId => {
    const suggestion = suggestions[itemId];
    if (!suggestion?.suggested_cost_type_id) return;

    try {
      const { error } = await supabase
        .from('cost_type_mapping_suggestions')
        .update({ status: 'approved' })
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'approved' },
      }));
    } catch (err) {
      console.error('Error approving suggestion:', err);
      alert('שגיאה באישור: ' + err.message);
    }
  };

  // Reject an AI suggestion
  const handleReject = async itemId => {
    const suggestion = suggestions[itemId];
    if (!suggestion) return;

    try {
      const { error } = await supabase
        .from('cost_type_mapping_suggestions')
        .update({ status: 'rejected' })
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'rejected' },
      }));
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      alert('שגיאה בדחייה: ' + err.message);
    }
  };

  // Manual override → write to suggestions table as 'manual'
  const handleManualMap = async (itemId, costTypeId) => {
    if (!costTypeId) return;

    try {
      const { error } = await supabase.from('cost_type_mapping_suggestions').upsert(
        {
          master_item_id: itemId,
          suggested_cost_type_id: costTypeId,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
        { onConflict: 'master_item_id' },
      );
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          master_item_id: itemId,
          suggested_cost_type_id: costTypeId,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
      }));
    } catch (err) {
      console.error('Error setting manual mapping:', err);
      alert('שגיאה בשמירה: ' + err.message);
    }
  };

  // Clear a mapping (delete suggestion)
  const handleClearMapping = async itemId => {
    try {
      const { error } = await supabase
        .from('cost_type_mapping_suggestions')
        .delete()
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } catch (err) {
      console.error('Error clearing mapping:', err);
      alert('שגיאה במחיקה: ' + err.message);
    }
  };

  // Batch approve all high-confidence suggestions (≥0.8)
  const handleBatchApprove = async () => {
    const highConfidence = masterItems.filter(i => {
      const s = suggestions[i.id];
      return s && s.status === 'pending' && s.confidence >= 0.8 && !i.cost_type_id;
    });

    if (highConfidence.length === 0) {
      alert('אין הצעות ברמת ביטחון גבוהה לאישור');
      return;
    }

    if (!window.confirm(`לאשר ${highConfidence.length} הצעות ברמת ביטחון גבוהה (≥0.8)?`)) return;

    for (const item of highConfidence) {
      await handleApprove(item.id);
    }
  };

  // Batch apply: write all approved/manual suggestions to master_items.cost_type_id
  const handleBatchApply = async () => {
    const toApply = masterItems.filter(i => {
      const s = suggestions[i.id];
      return (
        s &&
        (s.status === 'approved' || s.status === 'manual') &&
        i.cost_type_id !== s.suggested_cost_type_id
      );
    });

    if (toApply.length === 0) {
      alert('אין מיפויים מאושרים להחלה');
      return;
    }

    if (!window.confirm(`להחיל ${toApply.length} מיפויים מאושרים על master_items?`)) return;

    let applied = 0;
    for (const item of toApply) {
      const s = suggestions[item.id];
      try {
        const { error } = await supabase
          .from('master_items')
          .update({ cost_type_id: s.suggested_cost_type_id })
          .eq('id', item.id);
        if (error) throw error;

        setMasterItems(prev =>
          prev.map(i => (i.id === item.id ? { ...i, cost_type_id: s.suggested_cost_type_id } : i)),
        );
        applied++;
      } catch (err) {
        console.error(`Error applying mapping for ${item.item_name}:`, err);
      }
    }

    alert(`הוחלו ${applied} מיפויים בהצלחה`);
  };

  // ─── Claude AI ───────────────────────────────────────────────────

  const runAiMapping = async () => {
    if (!apiKey) {
      alert('נא להזין Claude API Key');
      return;
    }

    // Only map items without suggestions AND without existing cost_type_id
    const unmappedItems = masterItems.filter(i => !isMapped(i.id) && !suggestions[i.id]);
    if (unmappedItems.length === 0) {
      alert('כל הפריטים כבר ממופים או יש להם הצעות');
      return;
    }

    setAiRunning(true);
    setAiProgress({ current: 0, total: unmappedItems.length });

    // Build cost type list for the prompt
    const ctList = costTypes.map(ct => `"${ct.name}" [id: ${ct.id}]`).join('\n');

    // Process in batches of 10
    const batchSize = 10;
    for (let batchStart = 0; batchStart < unmappedItems.length; batchStart += batchSize) {
      const batch = unmappedItems.slice(batchStart, batchStart + batchSize);
      setAiProgress({ current: batchStart, total: unmappedItems.length });

      // Rate limit between batches
      if (batchStart > 0) await new Promise(r => setTimeout(r, 2000));

      try {
        const itemsList = batch
          .map((item, idx) => {
            const typeName = itemTypes[item.item_type_id] || 'לא ידוע';
            const bundleInfo = item.is_bundle
              ? ` (חבילה${item.bundle_type ? ` - ${item.bundle_type}` : ''})`
              : '';
            const costInfo = item.estimated_cost
              ? ` [עלות משוערת: ₪${item.estimated_cost}]`
              : ' [ללא עלות]';
            return `${idx + 1}. "${item.item_name}" (סוג: ${typeName})${bundleInfo}${costInfo}`;
          })
          .join('\n');

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 1500,
            messages: [
              {
                role: 'user',
                content: `You are helping classify Israeli construction/renovation items by their cost/pricing type.

Each item needs a cost type that describes HOW it is priced/measured. For example:
- "מחיר ליחידה" (per unit) — for discrete items like doors, toilets, sinks, alarm systems
- "מחיר למטר רבוע" (per sqm) — for area-based items like flooring, wall finishes
- "מחיר למטר" (per meter) — for linear items like fences, countertops
- "מחיר קומפלט" (complete/lump sum) — for complete systems like HVAC, smart home, pools
- "מחיר לנקודה" (per point) — for electrical/plumbing points

IMPORTANT: Bundle items (חבילה) are parent containers that group child items together.
- Bundle wrappers with [ללא עלות] usually need "מחיר קומפלט" as a placeholder.
- Choice bundles let the user pick ONE child, so the parent itself is a container.

Items to map:
${itemsList}

Available cost types:
${ctList}

For each item, pick the BEST matching cost type ID.
Provide a confidence score (0.0 to 1.0) — use 0.9+ for obvious matches, 0.5-0.8 for reasonable guesses.

Respond ONLY as a JSON array, one object per item, in order:
[{"item_index": 1, "cost_type_id": "uuid-here", "confidence": 0.9, "reasoning": "brief Hebrew explanation"}]`,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errText = await response.text();
          console.error(`[AI COST TYPE] API error:`, errText);
          continue;
        }

        const data = await response.json();
        const text = data.content?.[0]?.text || '';

        // Parse the JSON array response
        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
          console.error(`[AI COST TYPE] Could not parse response:`, text);
          continue;
        }

        const parsed = JSON.parse(jsonMatch[0]);

        // Save each suggestion to the DB
        for (const mapping of parsed) {
          const itemIdx = mapping.item_index - 1;
          if (itemIdx < 0 || itemIdx >= batch.length) continue;

          const item = batch[itemIdx];
          const ctId = mapping.cost_type_id;

          // Verify the cost type ID exists
          if (!costTypeMap[ctId]) {
            console.error(`[AI COST TYPE] Unknown cost type ID "${ctId}" for "${item.item_name}"`);
            continue;
          }

          console.log(
            `[AI COST TYPE] ✓ "${item.item_name}" → ${costTypeMap[ctId].name} (${mapping.confidence}) — ${mapping.reasoning}`,
          );

          const { error: insertError } = await supabase
            .from('cost_type_mapping_suggestions')
            .upsert(
              {
                master_item_id: item.id,
                suggested_cost_type_id: ctId,
                confidence: mapping.confidence || 0.5,
                ai_reasoning: mapping.reasoning,
                status: 'pending',
              },
              { onConflict: 'master_item_id' },
            );

          if (insertError) {
            console.error(`Error saving suggestion for ${item.item_name}:`, insertError);
            continue;
          }

          // Update local state
          setSuggestions(prev => ({
            ...prev,
            [item.id]: {
              master_item_id: item.id,
              suggested_cost_type_id: ctId,
              confidence: mapping.confidence || 0.5,
              ai_reasoning: mapping.reasoning,
              status: 'pending',
            },
          }));
        }
      } catch (err) {
        console.error(`Error processing batch:`, err);
      }
    }

    setAiProgress({ current: unmappedItems.length, total: unmappedItems.length });
    setAiRunning(false);
  };

  // ─── Render helpers ────────────────────────────────────────────

  const getConfidenceClass = confidence => {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  };

  // ─── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bb-mapping-page">
        <div className="bb-mapping-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="bb-mapping-page">
      {/* Header */}
      <div className="bb-mapping-header">
        <h1>מיפוי פריטים לסוג עלות</h1>
        <div className="bb-mapping-header-actions">
          <input
            type="text"
            className="bb-mapping-search"
            placeholder="חיפוש פריט..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Progress */}
      <div className="bb-mapping-progress">
        <div className="bb-mapping-progress-bar">
          <div
            className="bb-mapping-progress-fill"
            style={{ width: `${stats.total ? (stats.mapped / stats.total) * 100 : 0}%` }}
          />
        </div>
        <div className="bb-mapping-progress-text">
          {stats.mapped}/{stats.total} ממופים
          {stats.pending > 0 && ` | ${stats.pending} ממתינים לאישור`}
          {` | ${stats.unmappedNonBundles} פריטים רגילים חסרים`}
        </div>
      </div>

      {/* AI actions */}
      <div className="api-key-section">
        <span className="api-key-label">
          Claude API: {apiKey ? '✓ מחובר' : '✗ חסר REACT_APP_CLAUDE_API_KEY ב-.env'}
        </span>
        <button
          className="bb-action-btn primary"
          onClick={runAiMapping}
          disabled={aiRunning || !apiKey}
        >
          {aiRunning
            ? `ממפה... (${aiProgress.current}/${aiProgress.total})`
            : `הרץ מיפוי AI (${stats.unmapped - stats.pending} חסרים)`}
        </button>
        <button className="bb-action-btn approve" onClick={handleBatchApprove} disabled={aiRunning}>
          אשר הכל (ביטחון ≥0.8)
        </button>
        <button className="bb-action-btn primary" onClick={handleBatchApply} disabled={aiRunning}>
          החל מאושרים על master_items
        </button>
      </div>

      {/* Filters */}
      <div className="bb-mapping-filters">
        {[
          { key: 'unmapped', label: `לא ממופים (${stats.unmapped})` },
          { key: 'all', label: `הכל (${stats.total})` },
          { key: 'non-bundles', label: `פריטים רגילים (${stats.unmappedNonBundles})` },
          {
            key: 'bundles',
            label: `חבילות (${masterItems.filter(i => i.is_bundle && !isMapped(i.id)).length})`,
          },
          { key: 'pending', label: `ממתינים (${stats.pending})` },
          { key: 'mapped', label: `ממופים (${stats.mapped})` },
        ].map(f => (
          <button
            key={f.key}
            className={`bb-mapping-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bb-mapping-table-container">
        <table className="bb-mapping-table">
          <thead>
            <tr>
              <th>#</th>
              <th>שם פריט</th>
              <th>סוג</th>
              <th>חבילה?</th>
              <th>עלות</th>
              <th>הצעת AI</th>
              <th>ביטחון</th>
              <th>מיפוי נוכחי</th>
              <th>נימוק</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={9} className="bb-mapping-empty">
                  אין פריטים להצגה
                </td>
              </tr>
            ) : (
              filteredItems.map((item, idx) => {
                const suggestion = suggestions[item.id];

                return (
                  <tr key={item.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <strong>{item.item_name}</strong>
                    </td>
                    <td>
                      <span className="item-type-badge">{itemTypes[item.item_type_id] || '-'}</span>
                    </td>
                    <td>
                      {item.is_bundle ? (
                        <span className="status-badge status-manual">
                          {item.bundle_type || 'חבילה'}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {item.estimated_cost
                        ? `₪${Number(item.estimated_cost).toLocaleString('he-IL')}`
                        : '-'}
                    </td>
                    <td>
                      <div className="bb-mapping-actions-cell">
                        {suggestion?.suggested_cost_type_id
                          ? costTypeMap[suggestion.suggested_cost_type_id]?.name || '-'
                          : '-'}
                        {suggestion?.status === 'pending' && suggestion?.suggested_cost_type_id && (
                          <>
                            <button
                              className="bb-action-btn approve"
                              onClick={() => handleApprove(item.id)}
                            >
                              אשר
                            </button>
                            <button
                              className="bb-action-btn reject"
                              onClick={() => handleReject(item.id)}
                            >
                              דחה
                            </button>
                          </>
                        )}
                        {suggestion?.status === 'rejected' && (
                          <span className="status-badge status-rejected">נדחה</span>
                        )}
                        {suggestion?.status === 'approved' && (
                          <span className="status-badge status-approved">אושר</span>
                        )}
                        {suggestion?.status === 'manual' && (
                          <span className="status-badge status-manual">ידני</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {suggestion?.confidence != null ? (
                        <span
                          className={`confidence-badge ${getConfidenceClass(suggestion.confidence)}`}
                        >
                          {(suggestion.confidence * 100).toFixed(0)}%
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <select
                          className={`chapter-select${getMappedCostTypeId(item.id) ? ' mapped' : ''}`}
                          value={getMappedCostTypeId(item.id)}
                          onChange={e => handleManualMap(item.id, e.target.value)}
                        >
                          <option value="">-- בחר סוג עלות --</option>
                          {costTypes.map(ct => (
                            <option key={ct.id} value={ct.id}>
                              {ct.name}
                            </option>
                          ))}
                        </select>
                        {getMappedCostTypeId(item.id) && (
                          <button
                            className="bb-action-btn reject"
                            style={{ flexShrink: 0 }}
                            onClick={() => handleClearMapping(item.id)}
                            title="נקה מיפוי"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {suggestion?.ai_reasoning ? (
                        <span className="ai-reasoning" title={suggestion.ai_reasoning}>
                          {suggestion.ai_reasoning}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CostTypeMappingPage;
